// Экран 5 из ТЗ раздел 3.3 — на фронте переименован в «Управление» (2026-08-20, запрос
// пользователя): теперь общий хаб действий над КАЖДОЙ активной подпиской пользователя отдельно —
// апгрейд (был и раньше, НАМЕРЕННО только переход на тариф дороже за оставшийся срок, см.
// app/api/routes/miniapp/change_plan.py), запланированное понижение (заглушка-кнопка "Понизить"
// рядом с "Перейти" — задача впереди, backend ещё не поддерживает) и автопродление (переехало
// сюда с "Моя подписка" — тот экран теперь только про устройства, этот — про сам тариф).
//
// Источник данных — fetchAllSubscriptions() (как на "Моя подписка"), НЕ одна "текущая": у
// пользователя может быть несколько активных подписок одновременно (несколько экземпляров
// одного тарифа или разные тарифы), каждая своя карточка со своим списком кандидатов на апгрейд
// и своим переключателем автопродления. subscription_id везде явный (changePlan/cancelAutoRenew/
// resumeAutoRenew в api/client.ts), backend проверяет владение через get_owned_subscription.
//
// Бандл-тарифы (Plan.is_bundle, сейчас только "Расширенный") исключены из списка кандидатов как
// цель — апгрейд между двумя подписками разных тарифных групп AV-Desk не спроектирован. Как
// ИСТОЧНИК бандл-подписка тоже не апгрейдится — backend отклонит понятной ошибкой, если
// попытаться (см. app/api/routes/miniapp/change_plan.py), отдельного предупреждения на экране
// сознательно нет (убрано 2026-08-20 по запросу пользователя).
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ApiError,
  cancelAutoRenew,
  changePlan,
  fetchAllSubscriptions,
  fetchPlans,
  resumeAutoRenew,
  type Plan,
  type Subscription,
} from "../api/client";
import { openInvoice } from "../api/openInvoice";

const STATUS_KEYS = {
  active: "status_active",
  expiring: "status_expiring",
  expired: "status_expired",
  cancelled: "status_cancelled",
} as const;

interface ManagementCardProps {
  subscription: Subscription;
  index: number;
  total: number;
  candidates: Plan[];
  onChanged: () => void;
}

function ManagementCard({ subscription, index, total, candidates, onChanged }: ManagementCardProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? "en-US" : "ru-RU";

  const [autoRenewSubmitting, setAutoRenewSubmitting] = useState(false);
  const [busyPlanId, setBusyPlanId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleToggleAutoRenew = () => {
    setAutoRenewSubmitting(true);
    setError(null);
    const action = subscription.auto_renew ? cancelAutoRenew : resumeAutoRenew;
    action(subscription.id)
      .then(() => onChanged())
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setAutoRenewSubmitting(false));
  };

  const handleUpgrade = (planId: number) => {
    setBusyPlanId(planId);
    setError(null);
    changePlan(planId, subscription.id)
      .then(({ invoice_link }) => {
        openInvoice(invoice_link, (status) => {
          setBusyPlanId(null);
          if (status === "paid") {
            onChanged();
          } else if (status !== "cancelled") {
            setError(t("change_plan_payment_failed"));
          }
        });
      })
      .catch((err: unknown) => {
        setBusyPlanId(null);
        setError(err instanceof ApiError ? err.message : String(err));
      });
  };

  return (
    <li className="subscription-card">
      {total > 1 && (
        <p>
          <strong>#{index + 1}</strong>
        </p>
      )}
      {error && <p role="alert">{error}</p>}
      <p>
        {t("subscription_status")}: {t(STATUS_KEYS[subscription.status])}
      </p>
      <p>
        {t("subscription_expires_at")}:{" "}
        {new Date(subscription.expires_at).toLocaleDateString(dateLocale)}
      </p>

      <p className="cancel-auto-renew-row">
        {t("cancel_auto_renew_status")}:{" "}
        <strong>
          {subscription.auto_renew ? t("cancel_auto_renew_on") : t("cancel_auto_renew_off")}
        </strong>
      </p>
      {!subscription.auto_renew && <p>{t("cancel_note_still_active")}</p>}
      <button type="button" onClick={handleToggleAutoRenew} disabled={autoRenewSubmitting}>
        {autoRenewSubmitting
          ? t("loading")
          : subscription.auto_renew
            ? t("cancel_action_cancel")
            : t("cancel_action_resume")}
      </button>

      <h4>{t("change_plan_switch_title")}</h4>
      <p>{t("change_plan_note")}</p>
      {candidates.length === 0 ? (
        <p>{t("change_plan_no_candidates")}</p>
      ) : (
        <ul>
          {candidates.map((plan) => (
            <li key={plan.id}>
              <strong>{plan.title}</strong> — {plan.price} {plan.currency} /{" "}
              {plan.duration_days} {t("days_short")}{" "}
              <button
                type="button"
                onClick={() => handleUpgrade(plan.id)}
                disabled={busyPlanId === plan.id}
              >
                {busyPlanId === plan.id ? t("loading") : t("change_plan_switch")}
              </button>{" "}
              <button type="button" disabled title={t("change_plan_downgrade_planned")}>
                {t("change_plan_downgrade")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function ChangePlanScreen() {
  const { t } = useTranslation();
  const [subscriptions, setSubscriptions] = useState<Subscription[] | undefined>(undefined);
  const [plans, setPlans] = useState<Plan[] | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const refreshSubscriptions = () =>
    fetchAllSubscriptions()
      .then(setSubscriptions)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)));

  useEffect(() => {
    refreshSubscriptions();
    fetchPlans()
      .then(setPlans)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- нужно только при монтировании
  }, []);

  const loading = subscriptions === undefined || plans === undefined;

  const groups = new Map<number, Subscription[]>();
  for (const sub of subscriptions ?? []) {
    const group = groups.get(sub.plan.id);
    if (group) {
      group.push(sub);
    } else {
      groups.set(sub.plan.id, [sub]);
    }
  }

  return (
    <div>
      <h1>{t("change_plan_title")}</h1>
      {error && <p role="alert">{error}</p>}
      {!error && loading && <p>{t("loading")}</p>}
      {!error && !loading && subscriptions!.length === 0 && <p>{t("subscription_none")}</p>}
      {[...groups.values()].map((group) => {
        const candidates = (plans ?? []).filter(
          (plan) => !plan.is_trial && !plan.is_bundle && plan.id !== group[0].plan.id,
        );
        return (
          <div key={group[0].plan.id}>
            <h2>
              {group[0].plan.title}
              {group[0].plan.is_bundle ? "*" : ""}{" "}
              {t("subscriptions_count", { count: group.length })}
            </h2>
            <ul>
              {group.map((sub, index) => (
                <ManagementCard
                  key={sub.id}
                  subscription={sub}
                  index={index}
                  total={group.length}
                  candidates={candidates}
                  onChanged={refreshSubscriptions}
                />
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
